import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import {
  GraduationCap, Loader2, Lock, ArrowRight, BookOpen, Zap, ShieldCheck, Sparkles,
} from 'lucide-react';

export default function TheorySection({ problemId }) {
  const { user } = useAuth();
  const hasAccess = !!user?.subscription_has_access;
  const [theory, setTheory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!hasAccess || !expanded || theory || loading) return;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/problems/${problemId}/theory`);
        setTheory(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Could not load theory');
      } finally {
        setLoading(false);
      }
    })();
  }, [expanded, hasAccess, problemId, theory, loading]);

  // Locked state — paywall
  if (!hasAccess) {
    return (
      <div data-testid="theory-paywall" className="industrial-card !p-6 border-t-4 border-t-sky-400 bg-gradient-to-br from-sky-500/10 via-lime-500/5 to-transparent glow-sky mb-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-sky-400 text-zinc-950 flex items-center justify-center shrink-0 shadow-lg shadow-sky-500/30">
            <GraduationCap size={22} strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="font-mono text-xs uppercase tracking-widest text-sky-400 mb-1 flex items-center gap-2">
              <Lock size={12} /> Academy · Locked
            </div>
            <div className="font-display text-2xl sm:text-3xl text-zinc-100 tracking-tight leading-tight">
              WHY THIS <span className="text-gradient-cool">WORKS</span>
            </div>
            <p className="text-zinc-400 text-sm mt-2 max-w-lg">
              Understand the theory behind this exact fix — trade codes (NEC, IPC, IRC), the physics, and the safety principles. Learn as you fix.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/account/subscribe" className="btn-primary text-sm hover-wiggle">
                <Sparkles size={14} /> Start 7-Day Free Trial <ArrowRight size={14} />
              </Link>
              <span className="font-mono text-[11px] text-zinc-500 uppercase tracking-widest self-center">
                $9.99/mo · Cancel anytime
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Unlocked but not yet expanded → tease
  if (!expanded) {
    return (
      <div data-testid="theory-unlocked-collapsed" className="industrial-card !p-5 border-l-4 border-l-lime-400 bg-gradient-to-r from-lime-500/5 to-transparent flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-lime-400 text-zinc-950 flex items-center justify-center shrink-0">
            <BookOpen size={20} strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-lime-400 flex items-center gap-1">
              <ShieldCheck size={11} /> Academy · Unlocked
            </div>
            <div className="font-display text-xl text-zinc-100 tracking-wide leading-none mt-0.5">
              LEARN WHY THIS WORKS
            </div>
            <div className="text-zinc-400 text-xs mt-1">
              Codes · theory · safety principles behind this exact fix
            </div>
          </div>
        </div>
        <button
          data-testid="theory-reveal-btn"
          onClick={() => setExpanded(true)}
          className="btn-lime text-sm hover-wiggle whitespace-nowrap"
        >
          <GraduationCap size={16} /> Show Me the Theory
        </button>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="industrial-card !p-6 border-t-4 border-t-lime-400 text-center mb-8">
        <Loader2 size={24} className="text-lime-400 animate-spin mx-auto mb-3" />
        <div className="font-mono text-xs uppercase tracking-widest text-lime-400">
          Teacher is prepping the lesson…
        </div>
      </div>
    );
  }

  if (error || !theory) {
    return (
      <div className="industrial-card !p-4 border-t-2 border-t-red-500 text-red-400 text-sm font-mono mb-8">
        {error || 'No theory available.'}
      </div>
    );
  }

  return (
    <div data-testid="theory-content" className="industrial-card !p-6 border-t-4 border-t-lime-400 mb-8 space-y-6 glow-lime">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-lime-400 mb-2 flex items-center gap-2">
          <GraduationCap size={12} /> Academy · Why This Works
        </div>
        <h3 className="font-display text-3xl text-zinc-100 tracking-tight">
          THE <span className="text-gradient-cool">THEORY</span>
        </h3>
      </div>

      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-amber-400 mb-1">Root Cause</div>
        <p className="text-zinc-300 leading-relaxed">{theory.why_it_happens}</p>
      </div>

      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-amber-400 mb-1">How Your Fix Works</div>
        <p className="text-zinc-300 leading-relaxed">{theory.how_the_fix_works}</p>
      </div>

      {theory.safety_theory && (
        <div className="border-l-4 border-l-amber-400 pl-4">
          <div className="font-mono text-[11px] uppercase tracking-widest text-amber-400 mb-1">Why Safety Matters</div>
          <p className="text-zinc-300 leading-relaxed text-sm">{theory.safety_theory}</p>
        </div>
      )}

      {theory.key_concepts?.length > 0 && (
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-sky-400 mb-3">Key Concepts</div>
          <div className="space-y-3">
            {theory.key_concepts.map((c, i) => (
              <div key={c.term || `kc-${i}`} className="border border-zinc-800 p-3">
                <div className="font-display text-lg text-lime-400 tracking-wide">{c.term}</div>
                <div className="text-zinc-300 text-sm mt-1">{c.definition}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {theory.code_references?.length > 0 && (
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-sky-400 mb-3">Code References</div>
          <div className="space-y-2">
            {theory.code_references.map((c, i) => (
              <div key={c.code || `cr-${i}`} className="flex items-start gap-3 border border-zinc-800 p-3">
                <div className="font-mono text-xs bg-amber-500/10 text-amber-400 border border-amber-500/40 px-2 py-1 shrink-0">
                  {c.code}
                </div>
                <div className="text-zinc-300 text-sm">{c.summary}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {theory.further_reading?.length > 0 && (
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Next Up</div>
          <ul className="space-y-1 text-zinc-400 text-sm">
            {theory.further_reading.map((r, i) => (
              <li key={typeof r === 'string' ? r : `fr-${i}`} className="flex items-start gap-2">
                <Zap size={12} className="text-amber-400 mt-1 shrink-0" /> {r}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
